import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Reservation, ReservationStatus, Table } from '../../interfaces';
import {
    RESERVATION_STATUS_META,
    RESERVATION_STATUS_ORDER,
    UseReservationsResult,
    lateGraceMinutes,
    lateStatus,
    minutesOfTime,
    waitStatus,
} from '../../hooks/useReservations';
import ReservationCard from './ReservationCard';
import WaitlistCard from './WaitlistCard';
import DateJumper from './DateJumper';

export type RailSide = 'left' | 'right';

interface Props {
    resv: UseReservationsResult;
    tables: Table[];
    /** Shared clock from the board, so queue waits tick with the table timers. */
    now: number;
    /**
     * Bookings whose table is running over its turn, and by how many minutes the
     * party is likely to be kept waiting. Measured on the board, where the clock is.
     */
    riskByReservationId: Record<string, number>;
    /** For a booking seated somewhere the host is not looking, the floor's name. */
    otherFloorByReservationId: Record<string, string>;
    side: RailSide;
    onSideChange: (side: RailSide) => void;
    onAssign: (r: Reservation) => void;
    onSeat: (r: Reservation) => void;
    onEdit: (r: Reservation) => void;
    onCreate: () => void;
    /** Open the sheet that puts a walk-in party into the queue. */
    onAddWaiting: () => void;
    /** Reopen that same sheet on a party already queued, to correct it. */
    onEditWaiting: (r: Reservation) => void;
    canCreate: boolean;
}

const icons = {
    prev: <svg viewBox='0 0 24 24' fill='none'><path d='m15 18-6-6 6-6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    next: <svg viewBox='0 0 24 24' fill='none'><path d='m9 6 6 6-6 6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    calendar: <svg viewBox='0 0 24 24' width='14' height='14' fill='none'><rect x='3' y='5' width='18' height='16' rx='2' stroke='currentColor' strokeWidth='1.8' /><path d='M3 10h18M8 3v4M16 3v4' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' /></svg>,
    plus: <svg viewBox='0 0 24 24' fill='none'><path d='M12 5v14M5 12h14' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' /></svg>,
    search: <svg viewBox='0 0 24 24' fill='none'><circle cx='11' cy='11' r='7' stroke='currentColor' strokeWidth='1.9' /><path d='m20 20-3.6-3.6' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' /></svg>,
    swap: <svg viewBox='0 0 24 24' fill='none'><path d='M7 8h13l-3-3M17 16H4l3 3' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    chevron: <svg viewBox='0 0 24 24' fill='none'><path d='m6 9 6 6 6-6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>,
};

const fmtWait = (m: number) => (m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`);

/**
 * The widths the rail settles on, narrowest first. Three named sizes rather than
 * any pixel value: a host dragging with a thumb cannot place a boundary to the
 * pixel, and nobody wants to. Dragging follows the finger and then lands on one
 * of these, and tapping the grip cycles them for anyone who would rather not drag.
 */
const RAIL_WIDTHS = [320, 400, 480];
const RAIL_WIDTH_KEY = 'tvResvWidth';
/** However wide it is asked to be, the floor plan keeps most of the screen. */
const RAIL_MAX_SHARE = 0.55;

const readStoredWidth = (): number | null => {
    const stored = Number(localStorage.getItem(RAIL_WIDTH_KEY));
    return RAIL_WIDTHS.includes(stored) ? stored : null;
};

const nearestWidth = (px: number, max: number): number => {
    const allowed = RAIL_WIDTHS.filter(w => w <= max);
    const options = allowed.length ? allowed : [RAIL_WIDTHS[0]];
    return options.reduce((best, w) => (Math.abs(w - px) < Math.abs(best - px) ? w : best), options[0]);
};

/** "Fri 27 Jun" / "Today" — short enough for the rail header. */
function formatDateLabel(dateKey: string, isToday: boolean): string {
    if (isToday) return 'Today';
    const d = new Date(`${dateKey}T12:00:00`);
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

const ReservationsRail: React.FC<Props> = ({
    resv,
    tables,
    now,
    riskByReservationId,
    otherFloorByReservationId,
    side,
    onSideChange,
    onAssign,
    onSeat,
    onEdit,
    onCreate,
    onAddWaiting,
    onEditWaiting,
    canCreate,
}) => {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [jumperOpen, setJumperOpen] = useState(false);
    /** Narrows the list to the parties nobody has seated yet. */
    const [lateOnly, setLateOnly] = useState(false);
    const dateBtnRef = useRef<HTMLButtonElement>(null);
    // The queue is only ever about right now, so it collapses away on other days.
    const [queueOpen, setQueueOpen] = useState(true);

    const railRef = useRef<HTMLElement>(null);
    const [width, setWidth] = useState<number | null>(readStoredWidth);
    /** Follows the finger mid-drag; null the rest of the time. */
    const [dragTo, setDragTo] = useState<number | null>(null);
    const drag = useRef<{ from: number; base: number; live: number; moved: boolean } | null>(null);

    const maxWidth = () => Math.round(window.innerWidth * RAIL_MAX_SHARE);

    const applyWidth = useCallback((px: number) => {
        const next = nearestWidth(px, maxWidth());
        setWidth(next);
        localStorage.setItem(RAIL_WIDTH_KEY, String(next));
    }, []);

    // A width chosen on a wide screen must not swallow a narrow one later.
    useEffect(() => {
        const fit = () => setWidth(w => (w && w > maxWidth() ? nearestWidth(maxWidth(), maxWidth()) : w));
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, []);

    const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
        const base = railRef.current?.getBoundingClientRect().width ?? RAIL_WIDTHS[0];
        drag.current = { from: e.clientX, base, live: base, moved: false };
        // Capture keeps the moves coming to the grip once the finger has left it.
        // It is refused if the pointer has already ended, which must not throw
        // away the drag itself.
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* keep going */ }
        setDragTo(base);
    };

    const moveDrag = (e: React.PointerEvent<HTMLDivElement>) => {
        const d = drag.current;
        if (!d) return;
        const travelled = e.clientX - d.from;
        // Widening means dragging away from the edge the rail is docked to.
        const wanted = d.base + (side === 'left' ? travelled : -travelled);
        if (Math.abs(travelled) > 4) d.moved = true;
        d.live = Math.max(RAIL_WIDTHS[0], Math.min(Math.min(RAIL_WIDTHS[RAIL_WIDTHS.length - 1], maxWidth()), Math.round(wanted)));
        setDragTo(d.live);
    };

    const endDrag = () => {
        const d = drag.current;
        drag.current = null;
        setDragTo(null);
        if (!d) return;
        // A press that never travelled is a tap, and a tap steps to the next size.
        if (!d.moved) {
            const allowed = RAIL_WIDTHS.filter(w => w <= maxWidth());
            const options = allowed.length ? allowed : [RAIL_WIDTHS[0]];
            const current = nearestWidth(d.base, maxWidth());
            applyWidth(options[(options.indexOf(current) + 1) % options.length]);
            return;
        }
        applyWidth(d.live);
    };

    const gripKeys = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        const allowed = RAIL_WIDTHS.filter(w => w <= maxWidth());
        const options = allowed.length ? allowed : [RAIL_WIDTHS[0]];
        const at = options.indexOf(nearestWidth(railRef.current?.getBoundingClientRect().width ?? options[0], maxWidth()));
        const dir = side === 'left' ? step : -step;
        applyWidth(options[Math.max(0, Math.min(options.length - 1, at + dir))]);
    };

    const cardFields = useMemo(
        () => resv.fields.filter(f => f.showOnCard),
        [resv.fields],
    );

    // Only offer status filters that actually have bookings, so the strip stays short.
    const statusChips = useMemo(
        () => RESERVATION_STATUS_ORDER.filter(s => resv.counts[s] > 0),
        [resv.counts],
    );

    const grace = lateGraceMinutes(resv.settings);

    /**
     * Parties the floor is still holding a table for. Counted off the whole day
     * rather than the filtered list, because being late is a fact about the clock:
     * a host who has filtered down to "Seated" still needs telling.
     */
    const lateBookings = useMemo(
        () => resv.onDate.filter(r => lateStatus(r, grace, now).late),
        [resv.onDate, grace, now],
    );

    // Nothing left to chase, so don't leave the host looking at an empty list.
    useEffect(() => {
        if (lateBookings.length === 0) setLateOnly(false);
    }, [lateBookings.length]);

    /**
     * Late-only answers exactly the number on the tile, ignoring the status and
     * search filters instead of intersecting with them — a count that promises
     * three and lists one is worse than no count at all.
     */
    const shown = lateOnly ? lateBookings : resv.visible;

    /** Group the list into time blocks so a busy service is scannable. */
    const groups = useMemo(() => {
        const out: { label: string; items: Reservation[] }[] = [];
        shown.forEach(r => {
            const hour = Math.floor(minutesOfTime(r.time) / 60);
            const label = `${`${hour}`.padStart(2, '0')}:00`;
            const last = out[out.length - 1];
            if (last && last.label === label) last.items.push(r);
            else out.push({ label, items: [r] });
        });
        return out;
    }, [shown]);

    // Search is the one filter the queue shares with the list: a host looking for
    // a name wants it found whether they are booked or standing at the door.
    const queue = useMemo(() => {
        const term = resv.searchTerm.trim().toLowerCase();
        if (!term) return resv.waitlist;
        return resv.waitlist.filter(r => (
            [r.code, r.guest?.firstName, r.guest?.lastName, r.guest?.phone]
                .filter(Boolean).join(' ').toLowerCase().includes(term)
        ));
    }, [resv.waitlist, resv.searchTerm]);

    // Covers and the longest wait, spelled out. Covers used to live in a tooltip
    // on the KPI tile, which on a touch screen means nowhere: there is nothing to
    // hover with, so the number simply did not exist on a tablet.
    const queueSummary = useMemo(() => {
        if (queue.length === 0) return '';
        const covers = queue.reduce((n, r) => n + r.partySize, 0);
        const longest = fmtWait(Math.max(...queue.map(r => waitStatus(r, now).waited)));
        return `${covers} covers · longest ${longest}`;
    }, [queue, now]);

    const handleSelect = (r: Reservation) => setActiveId(prev => (prev === r.id ? null : r.id));

    const shownWidth = dragTo ?? width;

    return (
        <aside
            ref={railRef}
            className={`resv-rail resv-rail--${side}`}
            aria-label='Reservations'
            style={shownWidth ? ({ ['--resv-rail-w']: `${shownWidth}px` } as React.CSSProperties) : undefined}
        >
            <div className='resv-rail__head'>
                <div className='resv-rail__headtop'>
                    <div className='resv-rail__title'>
                        Reservations
                        <span className='resv-rail__count'>{resv.kpis.bookings}</span>
                    </div>
                    <div className='resv-rail__headbtns'>
                        <button
                            type='button'
                            className={`resv-iconbtn${searchOpen ? ' is-active' : ''}`}
                            onClick={() => {
                                setSearchOpen(o => !o);
                                if (searchOpen) resv.setSearchTerm('');
                            }}
                            aria-label='Search reservations'
                            title='Search'
                        >{icons.search}</button>
                        <button
                            type='button'
                            className='resv-iconbtn'
                            onClick={() => onSideChange(side === 'left' ? 'right' : 'left')}
                            aria-label='Move panel to the other side'
                            title={`Move to the ${side === 'left' ? 'right' : 'left'}`}
                        >{icons.swap}</button>
                        {canCreate && (
                            <button
                                type='button'
                                className='resv-newbtn'
                                onClick={onCreate}
                                aria-label='New reservation'
                                title='New reservation'
                            >{icons.plus}New</button>
                        )}
                    </div>
                </div>

                {searchOpen && (
                    <input
                        className='resv-search'
                        placeholder='Name, phone, room or code'
                        value={resv.searchTerm}
                        onChange={(e) => resv.setSearchTerm(e.target.value)}
                        autoFocus
                    />
                )}

                <div className='resv-rail__datebar'>
                    <button type='button' className='resv-iconbtn' onClick={() => resv.stepDate(-1)} aria-label='Previous day'>{icons.prev}</button>
                    <button
                        ref={dateBtnRef}
                        type='button'
                        className={`resv-datebtn${resv.isToday ? ' is-today' : ''}${jumperOpen ? ' is-open' : ''}`}
                        onClick={() => setJumperOpen(o => !o)}
                        aria-expanded={jumperOpen}
                        title='Pick a date'
                    >
                        {icons.calendar}
                        {formatDateLabel(resv.dateKey, resv.isToday)}
                    </button>
                    <button type='button' className='resv-iconbtn' onClick={() => resv.stepDate(1)} aria-label='Next day'>{icons.next}</button>
                    {/* Only worth offering once there is somewhere to come back from. */}
                    {!resv.isToday && (
                        <button type='button' className='resv-todaybtn' onClick={resv.goToday} title='Back to today'>
                            Today
                        </button>
                    )}
                    {jumperOpen && (
                        <DateJumper
                            value={resv.dateKey}
                            anchor={dateBtnRef}
                            onPick={resv.setDateKey}
                            onClose={() => setJumperOpen(false)}
                        />
                    )}
                </div>

                {resv.serviceWindowsEnabled && resv.settings && (
                    <div className='resv-seg'>
                        <button
                            type='button'
                            className={`resv-seg__btn${resv.serviceId === 'all' ? ' is-active' : ''}`}
                            onClick={() => resv.setServiceId('all')}
                        >All day</button>
                        {resv.settings.services.filter(s => s.isActive).map(s => (
                            <button
                                key={s.id}
                                type='button'
                                className={`resv-seg__btn${resv.serviceId === s.id ? ' is-active' : ''}`}
                                onClick={() => resv.setServiceId(s.id)}
                            >{s.name}</button>
                        ))}
                    </div>
                )}

                <div className='resv-kpis'>
                    <div className='resv-kpi'>
                        <span className='resv-kpi__v'>{resv.kpis.covers}</span>
                        <span className='resv-kpi__k'>covers</span>
                    </div>
                    <div className='resv-kpi'>
                        <span className='resv-kpi__v'>{resv.kpis.bookings}</span>
                        <span className='resv-kpi__k'>booked</span>
                    </div>
                    <div className={`resv-kpi${resv.kpis.unassigned > 0 ? ' resv-kpi--alert' : ''}`}>
                        <span className='resv-kpi__v'>{resv.kpis.unassigned}</span>
                        <span className='resv-kpi__k'>no table</span>
                    </div>
                    {resv.kpis.waiting > 0 && (
                        <div className='resv-kpi resv-kpi--wait'>
                            <span className='resv-kpi__v'>{resv.kpis.waiting}</span>
                            <span className='resv-kpi__k'>waiting</span>
                        </div>
                    )}
                    {/* The alert and the way to act on it are the same control: a count
                      * of parties to chase is no use without the list behind it. */}
                    {lateBookings.length > 0 && (
                        <button
                            type='button'
                            className={`resv-kpi resv-kpi--late${lateOnly ? ' is-on' : ''}`}
                            onClick={() => setLateOnly(o => !o)}
                            aria-pressed={lateOnly}
                            title={lateOnly
                                ? 'Show the whole day again'
                                : `${lateBookings.length} booking${lateBookings.length > 1 ? 's' : ''} past ${grace} minutes late — show just those`}
                        >
                            <span className='resv-kpi__v'>{lateBookings.length}</span>
                            <span className='resv-kpi__k'>late</span>
                        </button>
                    )}
                </div>

                {statusChips.length > 1 && (
                    <div className='resv-chips'>
                        <button
                            type='button'
                            className={`resv-chip${resv.statusFilter === 'all' && !lateOnly ? ' is-active' : ''}`}
                            onClick={() => { resv.setStatusFilter('all'); setLateOnly(false); }}
                        >
                            All<span className='resv-chip__n'>{resv.counts.all}</span>
                        </button>
                        {statusChips.map(s => (
                            <button
                                key={s}
                                type='button'
                                className={`resv-chip${resv.statusFilter === s && !lateOnly ? ' is-active' : ''}`}
                                style={{ ['--accent' as any]: RESERVATION_STATUS_META[s].accent }}
                                onClick={() => {
                                    // Picking a status is a way out of the late-only list.
                                    setLateOnly(false);
                                    resv.setStatusFilter(resv.statusFilter === s ? 'all' : (s as ReservationStatus));
                                }}
                                title={`${RESERVATION_STATUS_META[s].label} · ${resv.counts[s]}`}
                            >
                                <span className='resv-chip__dot' />
                                {RESERVATION_STATUS_META[s].short}
                                <span className='resv-chip__n'>{resv.counts[s]}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className='resv-rail__list'>
                {/* The queue sits above the day: it is the only part of the rail that
                  * is about this minute, and it is what a host is asked about most. */}
                {(queue.length > 0 || resv.isToday) && (
                    <section className={`resv-queue${queueOpen ? ' is-open' : ''}`}>
                        <div className='resv-queue__head'>
                            <button
                                type='button'
                                className='resv-queue__toggle'
                                onClick={() => setQueueOpen(o => !o)}
                                aria-expanded={queueOpen}
                            >
                                <i className='resv-queue__chev'>{icons.chevron}</i>
                                Waiting
                                <span className='resv-queue__n'>{queue.length}</span>
                                {queueSummary && <span className='resv-queue__sum'>{queueSummary}</span>}
                            </button>
                            {canCreate && resv.isToday && (
                                <button
                                    type='button'
                                    className='resv-queue__add'
                                    onClick={onAddWaiting}
                                    title='Add a walk-in to the queue'
                                >{icons.plus}Walk-in</button>
                            )}
                        </div>

                        {queueOpen && queue.length === 0 && (
                            <div className='resv-queue__empty'>Nobody waiting.</div>
                        )}

                        {queueOpen && queue.map((r, i) => (
                            <WaitlistCard
                                key={r.id}
                                entry={r}
                                position={i + 1}
                                now={now}
                                isActive={activeId === r.id}
                                onSelect={handleSelect}
                                onSeat={onSeat}
                                onCall={resv.callWaiting}
                                onEdit={onEditWaiting}
                                onRequote={resv.requoteWait}
                                onLeave={resv.leaveWaitlist}
                            />
                        ))}
                    </section>
                )}

                {resv.loading && shown.length === 0 && (
                    <div className='resv-empty'><span className='resv-empty__title'>Loading…</span></div>
                )}

                {!resv.loading && shown.length === 0 && (
                    <div className='resv-empty'>
                        <span className='resv-empty__title'>No reservations</span>
                        <span>
                            {resv.searchTerm
                                ? 'Nothing matches that search.'
                                : resv.serviceId === 'all'
                                    ? 'Nothing booked for this day yet.'
                                    : 'Nothing booked for this service.'}
                        </span>
                    </div>
                )}

                {groups.map(g => (
                    <div key={g.label} className='resv-group'>
                        <div className='resv-group__label'>{g.label}</div>
                        {g.items.map(r => (
                            <ReservationCard
                                key={r.id}
                                reservation={r}
                                cardFields={cardFields}
                                tables={tables}
                                riskMinutes={riskByReservationId[r.id] ?? 0}
                                late={lateStatus(r, grace, now)}
                                otherFloor={otherFloorByReservationId[r.id]}
                                isActive={activeId === r.id}
                                isAssigning={resv.assigning?.id === r.id}
                                onSelect={handleSelect}
                                onAssign={onAssign}
                                onSeat={onSeat}
                                onEdit={onEdit}
                                onHold={resv.holdLate}
                                onNoShow={(booking) => resv.setStatus(booking, 'noShow')}
                            />
                        ))}
                    </div>
                ))}
            </div>

            {/* Drag it to resize, or tap it to step through the sizes: a thumb on
              * a moving boundary is imprecise, so neither has to be accurate. */}
            <div
                className={`resv-grip${dragTo !== null ? ' is-dragging' : ''}`}
                role='separator'
                aria-orientation='vertical'
                aria-label='Resize the reservations panel'
                aria-valuenow={shownWidth ?? undefined}
                tabIndex={0}
                onPointerDown={startDrag}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onKeyDown={gripKeys}
                title='Drag to resize, tap to change size'
            >
                <span className='resv-grip__handle' aria-hidden='true'>
                    <span /><span /><span />
                </span>
            </div>
        </aside>
    );
};

export default ReservationsRail;
